import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, CheckCircle2, XCircle, MapPin, Truck, X, CloudOff, RefreshCw, ScanLine } from "lucide-react";
import EscanerRemision from "@/components/EscanerRemision";
import IncidenciaCaptura, { incidenciaValida } from "@/components/IncidenciaCaptura";
import { RESULTADO_CLS } from "@/components/HistorialEntregas";
import { encolarEntrega, listarCola, procesarCola } from "@/lib/colaEntregas";
import { fechaMX } from "@/lib/format";

const MOTIVOS = ["No estaba el cliente", "Cerrado", "El cliente ya no lo quiere", "El cliente rechazó la mercancía", "Otro"];

function Fotos({ files, setFiles }) {
  const ref = useRef(null);
  const agregar = (e) => {
    const nuevos = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length + nuevos.length > 5) return toast.error("Máximo 5 hojas por remisión");
    setFiles([...files, ...nuevos]);
  };
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={agregar} data-testid="entrega-foto-input" />
      <div className="grid grid-cols-3 gap-2">
        {files.map((f, i) => (
          <div key={i} className="relative aspect-[3/4] rounded-md border bg-muted overflow-hidden" data-testid={`entrega-foto-${i}`}>
            {f.type.startsWith("image/") ? <img src={URL.createObjectURL(f)} alt={`Hoja ${i + 1}`} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center text-xs p-1 text-center">{f.name}</div>}
            <button type="button" className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`entrega-foto-quitar-${i}`}><X className="h-3 w-3" /></button>
            <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1.5 rounded">Hoja {i + 1}</span>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" className="w-full h-12" onClick={() => ref.current?.click()} disabled={files.length >= 5} data-testid="entrega-tomar-foto">
        <Camera className="h-5 w-5 mr-2" />{files.length ? "Agregar otra hoja" : "Tomar foto de la remisión"}
      </Button>
    </div>
  );
}

function RegistrarEntrega({ entrega, onClose, onDone }) {
  const [resultado, setResultado] = useState(null);
  const [files, setFiles] = useState([]);
  const [escaneo, setEscaneo] = useState(null);
  const [escaneando, setEscaneando] = useState(false);
  const [completa, setCompleta] = useState(null);
  const [incid, setIncid] = useState({ items: [], nota: "" });
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  if (!entrega) return null;
  const confirmar = async () => {
    if (resultado === "Entregada" && !escaneo) return toast.error("Escanea la remisión firmada");
    if (resultado === "Rechazada" && !motivo) return toast.error("Elige el motivo del rechazo");
    setSaving(true);
    const payload = { resultado, motivo: resultado === "Rechazada" ? motivo : null, nota, captura_manual: resultado === "Entregada" ? !!escaneo?.manual : false };
    if (resultado === "Entregada" && completa === null) { setSaving(false); return toast.error("Indica si la entrega fue completa"); }
    if (resultado === "Entregada" && completa === false) {
      if (!incidenciaValida(incid)) { setSaving(false); return toast.error("Completa qué pasó, el modelo y los atados de cada problema"); }
      payload.incidencia = { items: incid.items.map((i) => ({ ...i, atados: Number(i.atados) })), nota: incid.nota };
    }
    await encolarEntrega(entrega.id, resultado === "Entregada" ? [escaneo.file] : files, payload);
    const r = await procesarCola();
    const mio = r.resultados?.find((x) => x.entregaId === entrega.id);
    setSaving(false);
    if (mio?.ok) toast.success(resultado === "Rechazada" ? "Rechazo registrado" : payload.incidencia ? "Entrega con incidencia registrada" : "Entrega registrada");
    else if (mio && !mio.ok) toast.error(`No se pudo registrar: ${mio.error}`);
    else toast.warning("Sin señal: la entrega se guardó en tu celular y se enviará sola al recuperar la señal.");
    onDone();
  };
  return (
    <>
    {escaneando && <EscanerRemision nombre={`Remision ${entrega.numero_remision || ""}.pdf`} onCancel={() => setEscaneando(false)} onDone={(r) => { setEscaneo(r); setEscaneando(false); }} />}
    <Dialog open={!escaneando} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92dvh] overflow-y-auto" data-testid="registrar-entrega-dialog">
        <DialogHeader><DialogTitle>{entrega.cliente}</DialogTitle><DialogDescription>{entrega.numero_remision} · {entrega.direccion}</DialogDescription></DialogHeader>
        <Label>¿Cómo fue la entrega?</Label>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant={resultado === "Entregada" ? "default" : "outline"} className={`h-16 flex-col ${resultado === "Entregada" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} onClick={() => setResultado("Entregada")} data-testid="entrega-opcion-entregada"><CheckCircle2 className="h-5 w-5" />Entregada</Button>
          <Button type="button" variant={resultado === "Rechazada" ? "default" : "outline"} className={`h-16 flex-col ${resultado === "Rechazada" ? "bg-red-600 hover:bg-red-700" : ""}`} onClick={() => setResultado("Rechazada")} data-testid="entrega-opcion-rechazada"><XCircle className="h-5 w-5" />No la recibió</Button>
        </div>
        {resultado === "Entregada" && (
          <>
            <Label>Remisión firmada</Label>
            {escaneo ? (
              <div className="flex items-center gap-3 rounded-md border p-2" data-testid="entrega-escaneo-listo">
                <img src={escaneo.portada} alt="Remisión escaneada" className="h-20 w-16 object-cover rounded border bg-white" />
                <div className="flex-1 text-sm"><p className="font-semibold">PDF escaneado</p><p className="text-xs text-muted-foreground">{escaneo.paginas} hoja(s){escaneo.manual ? " · captura manual" : ""}</p></div>
                <Button type="button" variant="outline" size="sm" onClick={() => setEscaneando(true)} data-testid="entrega-reescanear">Repetir</Button>
              </div>
            ) : (
              <Button type="button" className="w-full h-14" onClick={() => setEscaneando(true)} data-testid="entrega-escanear"><ScanLine className="h-5 w-5 mr-2" />Escanear remisión</Button>
            )}
            <Label>¿La entrega fue completa?</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={completa === true ? "default" : "outline"} className={completa === true ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setCompleta(true)} data-testid="entrega-completa-si">Sí, completa</Button>
              <Button type="button" variant={completa === false ? "default" : "outline"} className={completa === false ? "bg-amber-500 hover:bg-amber-600 text-black" : ""} onClick={() => { setCompleta(false); if (!incid.items.length) setIncid({ ...incid, items: [{ tipo: "", modelo: (entrega.modelos || []).length === 1 ? entrega.modelos[0].modelo : "", atados: "" }] }); }} data-testid="entrega-completa-no">Hubo un problema</Button>
            </div>
            {completa === false && <IncidenciaCaptura modelos={entrega.modelos || []} valor={incid} onChange={setIncid} />}
          </>
        )}
        {resultado === "Rechazada" && (
          <div className="space-y-2">
            <Label>Motivo</Label>
            <Select value={motivo} onValueChange={setMotivo}><SelectTrigger data-testid="entrega-motivo"><SelectValue placeholder="Elige el motivo" /></SelectTrigger>
              <SelectContent>{MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            <Label>Foto (opcional)</Label><Fotos files={files} setFiles={setFiles} />
          </div>
        )}
        {resultado && <><Label>Nota (opcional)</Label><Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} data-testid="entrega-nota" />
          <Button className="w-full h-12" onClick={confirmar} disabled={saving} data-testid="entrega-confirmar">{saving ? "Enviando..." : "Confirmar"}</Button></>}
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function MisViajes() {
  const [viajes, setViajes] = useState([]);
  const [cola, setCola] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const cargar = useCallback(async () => {
    setCola((await listarCola()).map((c) => c.entregaId));
    try { const { data } = await api.get("/viajes"); setViajes(data || []); } catch { /* sin señal */ }
    setLoading(false);
  }, []);
  const sincronizar = useCallback(async () => { const r = await procesarCola(); if (r.resultados?.some((x) => x.ok)) toast.success("Entregas pendientes enviadas"); cargar(); }, [cargar]);
  useEffect(() => {
    sincronizar();
    window.addEventListener("online", sincronizar);
    const t = setInterval(sincronizar, 30000);
    return () => { window.removeEventListener("online", sincronizar); clearInterval(t); };
  }, [sincronizar]);
  return (
    <div className="space-y-5 max-w-2xl mx-auto" data-testid="mis-viajes-page">
      <div className="flex items-center justify-between gap-2">
        <div><h1 className="text-2xl font-bold">Mis viajes</h1><p className="text-sm text-muted-foreground">Entregas que tienes asignadas</p></div>
        <Button variant="outline" size="icon" onClick={sincronizar} title="Actualizar" data-testid="mis-viajes-actualizar"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {cola.length > 0 && <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-300 text-amber-800 p-3 text-sm" data-testid="cola-pendiente"><CloudOff className="h-4 w-4" />{cola.length} entrega(s) guardadas en tu celular, se enviarán al recuperar la señal.</div>}
      {loading && <p className="text-muted-foreground">Cargando...</p>}
      {!loading && viajes.length === 0 && <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground" data-testid="mis-viajes-vacio"><Truck className="h-8 w-8 mx-auto mb-2" />No tienes viajes asignados.</div>}
      {viajes.map((v) => (
        <div key={v.id} className="rounded-xl border bg-card" data-testid={`mi-viaje-${v.id}`}>
          <div className="p-4 border-b flex flex-wrap justify-between gap-2">
            <div><p className="font-bold">Viaje {v.folio}</p><p className="text-xs text-muted-foreground">{fechaMX(v.fecha)} · {v.camion ? `${v.camion.placas} ${v.camion.marca || ""}` : "Sin camión"}</p></div>
            <Badge variant="outline" className="h-fit" data-testid={`mi-viaje-avance-${v.id}`}>{v.hechas} de {v.total} entregadas</Badge>
          </div>
          <div className="divide-y">
            {v.entregas.map((e, i) => {
              const enCola = cola.includes(e.id);
              return (
                <div key={e.id} className="p-4 space-y-2" data-testid={`mi-entrega-${e.id}`}>
                  <div className="flex justify-between gap-2"><p className="font-semibold">{i + 1}. {e.cliente}</p>
                    {e.resultado ? <Badge className={RESULTADO_CLS[e.resultado]}>{e.resultado}</Badge> : enCola ? <Badge className="bg-amber-100 text-amber-800 border-transparent">Por enviar</Badge> : null}</div>
                  <p className="text-sm text-muted-foreground flex gap-1"><MapPin className="h-4 w-4 shrink-0 mt-0.5" />{e.direccion || "Sin dirección registrada"}</p>
                  <p className="text-xs text-muted-foreground">Remisión {e.numero_remision}</p>
                  {!e.resultado && !enCola && <Button className="w-full h-11" onClick={() => setSel(e)} data-testid={`mi-entrega-registrar-${e.id}`}>Registrar entrega</Button>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {sel && <RegistrarEntrega key={sel.id} entrega={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); cargar(); }} />}
    </div>
  );
}
